import { useCallback, useEffect, useId, useRef, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { mapApiErrorToUiModel, resolveUiErrorCopy } from '@formosa/shared/i18n/mapApiErrorToUiModel';
import { resolveSafeUserVisibleError } from '@formosa/shared/i18n/safeUserVisibleError';
import { journeyApi } from '../../api/journeyApi';
import { journeyBudgetApi } from '../../api/journeyBudgetApi';
import { useTravelTranslation } from '../../app/TravelLocaleContext';
import { useAuth } from '../../auth/AuthContext';
import { ErrorState, LoadingState } from '../../components/ui';
import { WORKSPACE_PRODUCT_CHROME } from '../journey-workspace/journeyWorkspacePresentation';
import {
  classifyJourneyAccessError,
  normalizeJourneyWorkspace,
} from '../journey-workspace/journeyWorkspaceModel';
import JourneyWorkspaceNav from '../journey-workspace/JourneyWorkspaceNav';
import {
  CostBudgetProgress,
  CostDisclaimer,
  CostMoneySummary,
} from '../journey-cost';
import { budgetCategoryLabel } from '../finance/budgetCategory';
import {
  BUDGET_CURRENCY_OPTIONS,
  BUDGET_VALIDATION_CODES,
  buildBudgetSetPayload,
  buildBudgetUpdatePayload,
  classifyBudgetError,
  draftFromBudget,
  emptyBudgetDraft,
  formatMoneyDisplay,
  normalizeBudgetSummary,
  normalizeJourneyBudget,
  previewAllocated,
  previewUnallocated,
  validateBudgetDraft,
} from './journeyBudgetModel';
import '../journey-workspace/journey-workspace.css';
import '../journey-workspace/journey-logistics.css';
import '../journey-cost/journey-cost.css';
import './journey-budget.css';

const WRITE_ROLES = new Set(['OWNER', 'COORDINATOR']);
const WRITABLE_LIFECYCLES = new Set(['DRAFT', 'READY', 'TRAVELING']);

const VALIDATION_MESSAGE_KEYS = Object.freeze({
  [BUDGET_VALIDATION_CODES.CURRENCY_REQUIRED]: 'travel.journeyCost.budget.validationErrors.currencyRequired',
  [BUDGET_VALIDATION_CODES.BASELINE_INVALID]: 'travel.journeyCost.budget.validationErrors.baselineInvalid',
  [BUDGET_VALIDATION_CODES.BASELINE_NEGATIVE]: 'travel.journeyCost.budget.validationErrors.baselineNegative',
  [BUDGET_VALIDATION_CODES.CATEGORIES_REQUIRED]: 'travel.journeyCost.budget.validationErrors.categoriesRequired',
  [BUDGET_VALIDATION_CODES.CATEGORY_UNKNOWN]: 'travel.journeyCost.budget.validationErrors.categoryUnknown',
  [BUDGET_VALIDATION_CODES.CATEGORY_AMOUNT_INVALID]: 'travel.journeyCost.budget.validationErrors.categoryAmountInvalid',
  [BUDGET_VALIDATION_CODES.CATEGORY_AMOUNT_NEGATIVE]: 'travel.journeyCost.budget.validationErrors.categoryAmountNegative',
  [BUDGET_VALIDATION_CODES.ALLOCATION_EXCEEDS_TOTAL]: 'travel.journeyCost.budget.validationErrors.allocationExceedsTotal',
});

/** 表單錯誤以 key／分類保存，渲染時才翻譯；控制流程不得依賴顯示文字。 */
function validationFormError(result) {
  return {
    key: VALIDATION_MESSAGE_KEYS[result.code] || 'travel.journeyCost.budget.validation',
    categoryCode: result.categoryCode,
  };
}

function formErrorText(error, t, tf) {
  if (!error) return '';
  if (error.text) return error.text;
  if (error.categoryCode !== undefined) {
    return tf(error.key, { category: budgetCategoryLabel(error.categoryCode, t) });
  }
  return t(error.key);
}

export default function JourneyBudgetPage() {
  const { journeyId } = useParams();
  const { user } = useAuth();
  const { t, tf } = useTravelTranslation();
  // 語系切換只重新渲染，不得觸發重新載入或清掉編輯中的草稿。
  const tRef = useRef(t);
  tRef.current = t;
  const viewerMemberId = user?.memberId ?? user?.id;
  const generation = useRef(0);
  const mutating = useRef(false);

  const [phase, setPhase] = useState('loading');
  const [errorModel, setErrorModel] = useState(null);
  const [journey, setJourney] = useState(null);
  const [budget, setBudget] = useState(null);
  const [summary, setSummary] = useState(null);
  const [mode, setMode] = useState('view'); // view | create | edit
  const [draft, setDraft] = useState(null);
  const [busy, setBusy] = useState(false);
  const [formError, setFormError] = useState(null);
  const [confirmSave, setConfirmSave] = useState(false);
  const currencyId = useId();
  const baselineId = useId();

  const load = useCallback(async ({ soft = false } = {}) => {
    const requestId = ++generation.current;
    if (!soft) {
      setPhase('loading');
      setErrorModel(null);
      setMode('view');
      setDraft(null);
      setConfirmSave(false);
    }
    setFormError(null);
    try {
      const detailPayload = await journeyApi.getDetail(journeyId);
      if (requestId !== generation.current) return;
      const nextJourney = normalizeJourneyWorkspace(detailPayload, viewerMemberId);
      setJourney(nextJourney);

      let budgetPayload = null;
      let summaryPayload = null;
      try {
        [budgetPayload, summaryPayload] = await Promise.all([
          journeyBudgetApi.get(journeyId),
          journeyBudgetApi.getSummary(journeyId),
        ]);
      } catch (cause) {
        if (requestId !== generation.current) return;
        const kind = classifyBudgetError(cause);
        if (kind === 'budget-not-found') {
          setBudget(null);
          setSummary(null);
          setPhase('empty');
          return;
        }
        throw cause;
      }
      if (requestId !== generation.current) return;
      setBudget(normalizeJourneyBudget(budgetPayload));
      setSummary(normalizeBudgetSummary(summaryPayload));
      setPhase('ready');
    } catch (cause) {
      if (requestId !== generation.current) return;
      const kind = classifyBudgetError(cause);
      const access = classifyJourneyAccessError(cause);
      if (kind === 'journey-not-found' || access === 'not-found') setPhase('not-found');
      else if (kind === 'forbidden' || access === 'forbidden') setPhase('forbidden');
      else {
        setErrorModel(mapApiErrorToUiModel(cause, { t: tRef.current }));
        setPhase('error');
      }
    }
  }, [journeyId, viewerMemberId]);

  useEffect(() => {
    if (!/^[1-9]\d*$/.test(journeyId || '')) {
      setPhase('not-found');
      return undefined;
    }
    load();
    return () => { generation.current += 1; };
  }, [journeyId, load]);

  const canWrite = WRITABLE_LIFECYCLES.has(journey?.lifecycleStatus)
    && WRITE_ROLES.has(journey?.requesterRole);

  function beginCreate() {
    setFormError(null);
    setConfirmSave(false);
    setDraft(emptyBudgetDraft(journey?.baseCurrency || 'TWD'));
    setMode('create');
  }

  function beginEdit() {
    if (!budget) return;
    setFormError(null);
    setConfirmSave(false);
    setDraft(draftFromBudget(budget));
    setMode('edit');
  }

  function cancelForm() {
    setFormError(null);
    setConfirmSave(false);
    setDraft(null);
    setMode('view');
  }

  function updateCategoryAmount(categoryCode, plannedAmount) {
    setDraft((current) => ({
      ...current,
      categories: current.categories.map((row) => (
        row.categoryCode === categoryCode ? { ...row, plannedAmount } : row
      )),
    }));
  }

  async function runSave() {
    if (mutating.current || !draft) return;
    const validation = validateBudgetDraft(draft);
    if (validation) {
      setFormError(validationFormError(validation));
      setConfirmSave(false);
      return;
    }
    mutating.current = true;
    setBusy(true);
    setFormError(null);
    const requestId = generation.current;
    try {
      if (mode === 'create') {
        await journeyBudgetApi.set(journey.journeyId, buildBudgetSetPayload(draft));
      } else {
        await journeyBudgetApi.update(
          journey.journeyId,
          buildBudgetUpdatePayload(draft, budget.rowVersion),
        );
      }
      if (requestId !== generation.current) return;
      setConfirmSave(false);
      setDraft(null);
      setMode('view');
      await load({ soft: true });
    } catch (cause) {
      if (requestId !== generation.current) return;
      const kind = classifyBudgetError(cause);
      if (kind === 'conflict') {
        setFormError({ key: 'travel.journeyCost.budget.conflict', reload: true });
        setConfirmSave(false);
        setMode('view');
        setDraft(null);
      } else if (kind === 'currency-mismatch') {
        setFormError({ key: 'travel.journeyCost.budget.currencyMismatch' });
        setConfirmSave(false);
      } else if (kind === 'validation') {
        setFormError({ key: 'travel.journeyCost.budget.validation' });
        setConfirmSave(false);
      } else if (kind === 'forbidden') {
        setFormError({ key: 'travel.journeyCost.budget.forbidden' });
        setConfirmSave(false);
      } else if (kind === 'lifecycle') {
        setFormError({ key: 'travel.journeyCost.budget.lifecycle' });
        setConfirmSave(false);
      } else if (kind === 'already-exists') {
        setFormError({ key: 'travel.journeyCost.budget.alreadyExists', reload: true });
        setConfirmSave(false);
      } else {
        setFormError({
          text: resolveSafeUserVisibleError(cause, t, 'travel.common.actionFailed')
            || t('travel.common.actionFailed'),
        });
        setConfirmSave(false);
      }
    } finally {
      mutating.current = false;
      setBusy(false);
    }
  }

  if (phase === 'loading') {
    return (
      <main className="journey-workspace-state">
        <LoadingState label={t('travel.common.loading')} data-testid="budget-loading" />
      </main>
    );
  }
  if (phase === 'not-found') {
    return (
      <main className="journey-workspace-state">
        <h1>{WORKSPACE_PRODUCT_CHROME.notFoundTitle}</h1>
        <p>{WORKSPACE_PRODUCT_CHROME.notFoundBody}</p>
        <Link to="/journeys">{WORKSPACE_PRODUCT_CHROME.backToMyJourneys}</Link>
      </main>
    );
  }
  if (phase === 'forbidden') {
    return (
      <main className="journey-workspace-state">
        <h1>{WORKSPACE_PRODUCT_CHROME.forbiddenTitle}</h1>
        <p>{WORKSPACE_PRODUCT_CHROME.forbiddenBody}</p>
        <Link to="/journeys">{WORKSPACE_PRODUCT_CHROME.backToMyJourneys}</Link>
      </main>
    );
  }
  if (phase === 'error') {
    const errorCopy = resolveUiErrorCopy(errorModel, t);
    return (
      <main className="journey-workspace-state" data-testid="budget-error">
        <ErrorState
          title={t('travel.journeyCost.budget.openFailed')}
          description={errorCopy.description}
          retryable
          retryLabel={t('travel.common.retry')}
          onRetry={load}
        />
      </main>
    );
  }

  const totals = summary || budget;
  const editing = mode === 'create' || mode === 'edit';
  const baselineAmount = totals?.baselineAmount || '0';
  // 權威 actual／spent 屬 Expense ledger；Budget 僅展示後端預算摘要的分配額（FIN-FE-1A 隔離）。
  const allocatedAmount = totals?.allocatedAmount || '0';
  const unallocatedAmount = totals?.unallocatedAmount || '0';
  const currencyCode = totals?.baseCurrency || budget?.baseCurrency || '';

  return (
    <main className="journey-workspace">
      <header className="journey-workspace__header">
        <div>
          <p className="journey-workspace__eyebrow">{WORKSPACE_PRODUCT_CHROME.eyebrow}</p>
          <h1>{journey.title}</h1>
          <dl className="journey-workspace__facts">
            {journey.destination ? <div><dt>目的地</dt><dd>{journey.destination}</dd></div> : null}
            {journey.dateRange ? <div><dt>日期</dt><dd>{journey.dateRange}</dd></div> : null}
            <div><dt>狀態</dt><dd>{journey.lifecycleLabel}</dd></div>
            {journey.requesterRoleLabel ? <div><dt>你的角色</dt><dd>{journey.requesterRoleLabel}</dd></div> : null}
          </dl>
        </div>
        <Link className="journey-workspace__back" to="/journeys">{WORKSPACE_PRODUCT_CHROME.myJourneys}</Link>
      </header>
      <JourneyWorkspaceNav journeyId={journey.journeyId} />

      <section className="journey-workspace__panel" aria-labelledby="budget-heading">
        <div className="journey-workspace__panel-head">
          <div>
            <h2 id="budget-heading">{WORKSPACE_PRODUCT_CHROME.budget}</h2>
          </div>
          <div className="journey-budget__toolbar journey-logistics__toolbar">
            <button type="button" onClick={() => load()} disabled={busy}>
              {t('travel.journeyCost.actions.refresh')}
            </button>
            {phase === 'ready' && canWrite && !editing ? (
              <button type="button" onClick={beginEdit} disabled={busy}>
                {t('travel.journeyCost.budget.edit')}
              </button>
            ) : null}
          </div>
        </div>

        {!editing ? (
          <CostDisclaimer text={t('travel.journeyCost.disclaimer')} />
        ) : null}

        {formError ? (
          <div className="journey-workspace__error" role="alert">
            <p>{formErrorText(formError, t, tf)}</p>
            {formError.reload ? (
              <button type="button" disabled={busy} onClick={() => load()}>
                {t('travel.journeyCost.actions.reloadBudget')}
              </button>
            ) : null}
          </div>
        ) : null}
        {!canWrite ? (
          <p className="journey-budget__readonly">
            {WRITABLE_LIFECYCLES.has(journey.lifecycleStatus)
              ? t('travel.journeyCost.budget.readonlyRole')
              : t('travel.journeyCost.budget.readonlyLifecycle')}
          </p>
        ) : null}

        {phase === 'empty' && !editing ? (
          <div className="journey-budget__empty">
            <p>{t('travel.journeyCost.budget.emptyTitle')}</p>
            {canWrite ? (
              <button type="button" disabled={busy} onClick={beginCreate}>
                {t('travel.journeyCost.budget.setup')}
              </button>
            ) : null}
          </div>
        ) : null}

        {phase === 'ready' && !editing && totals ? (
          <>
            <div className="journey-cost-summary-grid" data-testid="budget-money-summary">
              <CostMoneySummary
                title={t('travel.journeyCost.budget.total')}
                currency={currencyCode}
                amount={baselineAmount}
                meta={t('travel.journeyCost.budget.baseCurrencyHint')}
              />
              <CostMoneySummary
                title={t('travel.journeyCost.budget.allocated')}
                currency={currencyCode}
                amount={allocatedAmount}
                meta={t('travel.journeyCost.budget.allocatedHint')}
              />
              <CostMoneySummary
                title={t('travel.journeyCost.budget.unallocated')}
                currency={currencyCode}
                amount={unallocatedAmount}
                meta={t('travel.journeyCost.budget.unallocatedHint')}
              />
            </div>
            <CostBudgetProgress
              spent={allocatedAmount}
              total={baselineAmount}
              currency={currencyCode}
              usageLabel={t('travel.journeyCost.budget.allocated')}
            />
            <p className="journey-budget__ledger-hint">
              {t('travel.journeyCost.budget.actualOnExpenses')}
              {' '}
              <Link to={`/journeys/${journey.journeyId}/expenses`}>
                {t('travel.journeyCost.budget.openExpenses')}
              </Link>
            </p>
            <dl className="journey-budget__summary">
              <div>
                <dt>{t('travel.journeyCost.budget.currency')}</dt>
                <dd>{currencyCode}</dd>
              </div>
              <div>
                <dt>{t('travel.journeyCost.budget.allocated')}</dt>
                <dd>{formatMoneyDisplay(totals.allocatedAmount)} {currencyCode}</dd>
              </div>
              <div>
                <dt>{t('travel.journeyCost.budget.unallocated')}</dt>
                <dd>{formatMoneyDisplay(totals.unallocatedAmount)} {currencyCode}</dd>
              </div>
            </dl>
          </>
        ) : null}

        {phase === 'ready' && !editing && budget ? (
          <ul className="journey-budget__categories" aria-label={t('travel.journeyCost.budget.categoryLegend')}>
            {budget.categories.map((row) => (
              <li key={row.categoryCode}>
                <strong>{budgetCategoryLabel(row.categoryCode, t)}</strong>
                <span>{formatMoneyDisplay(row.plannedAmount)} {budget.baseCurrency}</span>
              </li>
            ))}
          </ul>
        ) : null}

        {editing && draft ? (
          <form
            className="journey-budget__form"
            noValidate
            onSubmit={(event) => {
              event.preventDefault();
              const validation = validateBudgetDraft(draft);
              if (validation) {
                setFormError(validationFormError(validation));
                return;
              }
              setConfirmSave(true);
            }}
          >
            <label htmlFor={currencyId}>
              {t('travel.journeyCost.budget.currency')}
              <select
                id={currencyId}
                value={draft.baseCurrency}
                disabled={busy}
                onChange={(e) => setDraft((current) => ({ ...current, baseCurrency: e.target.value }))}
              >
                {BUDGET_CURRENCY_OPTIONS.map((code) => (
                  <option key={code} value={code}>{code}</option>
                ))}
              </select>
            </label>

            <label htmlFor={baselineId}>
              {t('travel.journeyCost.budget.total')}
              <input
                id={baselineId}
                inputMode="decimal"
                value={draft.baselineAmount}
                disabled={busy}
                placeholder="0.00"
                onChange={(e) => setDraft((current) => ({ ...current, baselineAmount: e.target.value }))}
              />
            </label>

            <fieldset>
              <legend>{t('travel.journeyCost.budget.categoryLegend')}</legend>
              <ul className="journey-budget__categories">
                {draft.categories.map((row) => {
                  const fieldId = `${baselineId}-${row.categoryCode}`;
                  return (
                    <li key={row.categoryCode}>
                      <label htmlFor={fieldId}>
                        {budgetCategoryLabel(row.categoryCode, t)}
                        <input
                          id={fieldId}
                          inputMode="decimal"
                          value={row.plannedAmount}
                          disabled={busy}
                          onChange={(e) => updateCategoryAmount(row.categoryCode, e.target.value)}
                        />
                      </label>
                    </li>
                  );
                })}
              </ul>
            </fieldset>

            <div className="journey-budget__preview" aria-live="polite">
              <div>
                {t('travel.journeyCost.budget.allocatedPreview')}
                :
                {' '}
                {formatMoneyDisplay(previewAllocated(draft))}
                {' '}
                {draft.baseCurrency}
              </div>
              <div>
                {t('travel.journeyCost.budget.unallocatedPreview')}
                :
                {' '}
                {formatMoneyDisplay(previewUnallocated(draft))}
                {' '}
                {draft.baseCurrency}
              </div>
            </div>

            <div className="journey-budget__actions">
              <button type="submit" disabled={busy}>
                {mode === 'create'
                  ? t('travel.journeyCost.budget.create')
                  : t('travel.journeyCost.budget.save')}
              </button>
              <button type="button" disabled={busy} onClick={cancelForm}>
                {t('travel.journeyCost.actions.cancel')}
              </button>
            </div>
          </form>
        ) : null}
      </section>

      {confirmSave ? (
        <div className="journey-logistics__dialog" role="dialog" aria-modal="true" aria-labelledby="budget-confirm-title">
          <div className="journey-logistics__dialog-card">
            <h3 id="budget-confirm-title">
              {mode === 'create'
                ? t('travel.journeyCost.budget.confirmCreateTitle')
                : t('travel.journeyCost.budget.confirmReplaceTitle')}
            </h3>
            <p>
              {mode === 'create'
                ? t('travel.journeyCost.budget.confirmCreateBody')
                : t('travel.journeyCost.budget.confirmReplaceBody')}
            </p>
            <div className="journey-workspace__edit-actions">
              <button
                type="button"
                className="journey-budget__confirm-primary"
                disabled={busy}
                onClick={runSave}
              >
                {busy
                  ? t('travel.journeyCost.actions.saving')
                  : t('travel.journeyCost.actions.confirm')}
              </button>
              <button type="button" disabled={busy} onClick={() => setConfirmSave(false)}>
                {t('travel.journeyCost.actions.cancel')}
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </main>
  );
}
